import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
	Button,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import {
	listMonitoringWritingAssistantPersonas,
	setMonitoringPromptNoPersona,
	setMonitoringPromptPersonas,
} from "../../../services/monitoringApi";

export default function PromptPersonasModal({
	isOpen,
	onClose,
	onSaved,
	websiteId,
	promptRow,
	maxPersonas = 0,
}) {
	const [personas, setPersonas] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [selected, setSelected] = useState(null);
	const [noPersona, setNoPersona] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useLayoutEffect(() => {
		if (!isOpen || !promptRow) {
			setPersonas([]);
			setLoading(false);
			return;
		}
		setPersonas([]);
		setLoading(true);
	}, [isOpen, promptRow]);

	useEffect(() => {
		if (!isOpen || !promptRow) return undefined;
		setError("");
		setLoadError("");
		setSelected(null);
		setNoPersona(promptRow.runEnabled !== false);
		let cancelled = false;
		listMonitoringWritingAssistantPersonas(websiteId).then((res) => {
			if (cancelled) return;
			setLoading(false);
			if (!res.ok) {
				setPersonas([]);
				setLoadError(res.message || "Could not load personas.");
				return;
			}
			setPersonas(res.personas || []);
		});
		return () => {
			cancelled = true;
		};
	}, [isOpen, promptRow, websiteId]);

	const ticked = useMemo(() => {
		if (selected != null) return selected;
		const ids = promptRow?.personaIds || [];
		return ids.filter((id) => personas.some((p) => Number(p.id) === Number(id)));
	}, [selected, promptRow, personas]);

	function togglePersona(id) {
		const num = Number(id);
		setSelected((prev) => {
			const base = prev ?? ticked;
			if (base.includes(num)) return base.filter((x) => x !== num);
			if (maxPersonas > 0 && base.length >= maxPersonas) return base;
			return [...base, num];
		});
	}

	async function handleSave() {
		if (!promptRow) return;
		if (!noPersona && ticked.length === 0) {
			setError("Choose at least one: the no-persona run or a persona");
			return;
		}
		setBusy(true);
		setError("");
		const noPersonaChanged = noPersona !== (promptRow.runEnabled !== false);
		try {
			if (noPersonaChanged && noPersona) {
				const res = await setMonitoringPromptNoPersona(
					websiteId,
					promptRow.promptId,
					true
				);
				if (!res.ok) throw new Error(res.message || "Could not enable no-persona run");
			}
			const personasRes = await setMonitoringPromptPersonas(
				websiteId,
				promptRow.promptId,
				ticked
			);
			if (!personasRes.ok) {
				throw new Error(personasRes.message || "Could not save personas");
			}
			if (noPersonaChanged && !noPersona) {
				const res = await setMonitoringPromptNoPersona(
					websiteId,
					promptRow.promptId,
					false
				);
				if (!res.ok) throw new Error(res.message || "Could not disable no-persona run");
			}
			onSaved?.(
				ticked.length
					? `Saved — runs ${noPersona ? "with no persona and " : ""}as ${ticked.length} persona${ticked.length === 1 ? "" : "s"} from the next run.`
					: "Saved — runs with no persona (persona runs removed)."
			);
			onClose?.();
		} catch (err) {
			setError(err.message || "Could not save");
		} finally {
			setBusy(false);
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={busy ? undefined : onClose}
			centered
			modalClassName="monitoring__add-prompt-modal-overlay"
			className="monitoring__prompt-personas-modal monitoring__add-prompt-modal"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader toggle={busy ? undefined : onClose}>Attach personas</ModalHeader>
			<ModalBody>
				<div className="monitoring__prompt-personas-body">
					{promptRow && (
						<p className="monitoring__add-prompt-intro text-muted">{promptRow.prompt}</p>
					)}
					<p className="monitoring__field-help text-muted mb-0">
						Each persona runs as its own tracked prompt from the next run. At least one
						run must stay on.
					</p>
					<div className="monitoring__brand-mention-box">
						<label className="monitoring__check-row monitoring__check-row--start mb-0">
							<input
								type="checkbox"
								checked={noPersona}
								disabled={noPersona && ticked.length === 0}
								onChange={(e) => setNoPersona(e.target.checked)}
							/>
							<span className="monitoring__check-row-text">
								<strong>No persona</strong>
								<span className="monitoring__check-row-sub">
									The prompt as written, without persona context.
								</span>
							</span>
						</label>
					</div>
					<div className="monitoring__add-prompt-field">
						<span className="monitoring__field-label">Personas</span>
						{loading ? (
							<div
								className="monitoring__add-prompt-persona-loading"
								role="status"
								aria-live="polite"
							>
								<Spinner size="sm" />
								<span className="text-muted small">Loading personas…</span>
							</div>
						) : loadError ? (
							<p className="monitoring__form-error mb-0">{loadError}</p>
						) : personas.length === 0 ? (
							<p className="monitoring__field-help text-muted mb-0">
								No personas yet — create them under AI Writing Assistant → Settings.
							</p>
						) : (
							<ul className="list-unstyled mb-0 monitoring__persona-picker">
								{personas.map((persona) => {
									const id = Number(persona.id);
									const checked = ticked.includes(id);
									const atCap =
										maxPersonas > 0 && !checked && ticked.length >= maxPersonas;
									return (
										<li key={id}>
											<label className="monitoring__check-row monitoring__check-row--start">
												<input
													type="checkbox"
													checked={checked}
													disabled={atCap}
													onChange={() => togglePersona(id)}
												/>
												<span className="monitoring__check-row-text">
													<strong>{persona.name}</strong>
													{persona.profession &&
													persona.profession !== persona.name ? (
														<span className="text-muted">
															{" "}
															· {persona.profession}
														</span>
													) : null}
												</span>
											</label>
										</li>
									);
								})}
							</ul>
						)}
					</div>
					{error ? (
						<p className="monitoring__form-error mb-0" role="alert">
							{error}
						</p>
					) : null}
				</div>
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button color="primary" onClick={handleSave} disabled={busy || loading}>
					{busy ? <Spinner size="sm" className="me-2" /> : null}
					Save
				</Button>
			</ModalFooter>
		</Modal>
	);
}
